import type { ReactNode } from 'react';
import { openFileDraft } from '../../../lib/doc-viewer-presenter';
import { HOME_TAB_BI_PAGE } from '../../../lib/home-tab-connectors';
import { type WebOpportunity, useOpportunitiesRead } from '../../../lib/opportunities';
import { parkSuggestedTask } from '../../../lib/suggested-task-ack';
import type { SuggestedTask } from '../../../lib/suggested-tasks';
import { useAgentName } from '../../../lib/use-agent-name';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useOpenSideChat } from '../../../lib/use-open-side-chat';
import { useTranslation } from '../../../lib/i18n';
import { useWorkspaces } from '../../../lib/workspaces';
import { SuggestedTaskChips } from '../chat-surface/SuggestedTaskChips';
import { Button, Card, Typography } from '../ui';
import styles from './CategoryEmptyState.module.css';

// The empty states of the home's category pages (Figma 20018:118052, "[Desktop&Mobile] Empty States"). Only the
// HOME's category pages draw them; the standalone screens keep their own. Each body renders its category's
// component below in place of its toolbar and list, under a title-only `CategoryHeader tight`. The rules follow
// iOS/Android `HomeCategoryEmptyState`: a card shows only once every read behind it says empty.
//
// Every action reuses a door that already exists: the Tasks chips ARE the empty chat's suggested-task chips,
// the Goals cards are the agent's proposed opportunities with the seed every other opportunity CTA sends, and
// "Create a file" opens the same draft editor as the Files screen's "New file".

/** How many opportunity cards the Goals frame (21849:56439) lays side by side. */
const OPPORTUNITY_CARD_COUNT = 2;

type Illustration = 'buttonCard' | 'dotCard';

function CategoryEmptyState({
  illustration,
  dot,
  title,
  body,
  wide,
  children,
}: {
  illustration: Illustration;
  /** The dot card's dot, a palette token — Files is blue, Outreach is cyan, as their tiles are. */
  dot?: string;
  title: string;
  body?: string;
  /** Goals: its suggestion cards span the card, so the frame drops the bottom padding and opens up the gap. */
  wide?: boolean;
  children?: ReactNode;
}) {
  return (
    <Card variant="sunken" className={styles.card}>
      <div className={wide ? `${styles.root} ${styles.wide}` : styles.root}>
        <div className={styles.lead}>
          <EmptyIllustration kind={illustration} dot={dot} />
          <div className={styles.copy}>
            <p className={`sym-type-medium-semi-bold ${styles.title}`}>{title}</p>
            {body && (
              <Typography variant="heading-04" color="secondary" className={styles.body}>
                {body}
              </Typography>
            )}
          </div>
        </div>
        {children}
      </div>
    </Card>
  );
}

/**
 * Tasks (21849:57117). A chip tap SENDS, where in the empty chat it only prefills, because the home has no
 * composer to put the prompt in. It is still parked as acted-on, so the suggestion stops coming back.
 */
export function TasksEmptyState() {
  const { t } = useTranslation();
  const openSideChat = useOpenSideChat();
  return (
    <CategoryEmptyState illustration="buttonCard" title={t('home.overview.emptyState.tasks.title')}>
      <SuggestedTaskChips
        enabled
        layout="wrap"
        skin="card"
        bi={{ page_name: HOME_TAB_BI_PAGE, location: 'empty_tasks' }}
        header={<TryOne />}
        onPick={(task: SuggestedTask) => {
          parkSuggestedTask(task.label);
          openSideChat({ kind: 'new', message: task.prompt });
        }}
      />
    </CategoryEmptyState>
  );
}

/** Goals (21849:56439): up to two proposed opportunities, or "Create a goal" when the agent has none or the read failed. */
export function GoalsEmptyState() {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const openSideChat = useOpenSideChat();
  const { opportunities, failed } = useOpportunitiesRead();
  const offered = opportunities?.slice(0, OPPORTUNITY_CARD_COUNT) ?? [];

  return (
    <CategoryEmptyState
      illustration="buttonCard"
      title={t('home.overview.emptyState.goals.title')}
      body={offered.length > 0 ? t('home.overview.emptyState.tryOne') : undefined}
      wide={offered.length > 0}
    >
      {/* Still loading: nothing rather than the fallback CTA flashing before the cards arrive. */}
      {!opportunities && !failed ? null : offered.length > 0 ? (
        <div className={styles.opportunities}>
          {offered.map((opportunity) => (
            <OpportunityCard key={opportunity.id} opportunity={opportunity} />
          ))}
        </div>
      ) : (
        <Button
          onClick={() => {
            logPageClickBi({ page_name: HOME_TAB_BI_PAGE, button_name: 'empty_goals_create' });
            openSideChat({ kind: 'new', message: t('goals.createPrompt') });
          }}
        >
          {t('goals.empty.cta')}
        </Button>
      )}
    </CategoryEmptyState>
  );
}

/** Routines, for an account with none at all, recommended ones included. No frame of its own, so the Apps shape. */
export function RoutinesEmptyState({ onCreate }: { onCreate: () => void }) {
  const { t } = useTranslation();
  return (
    <CategoryEmptyState illustration="buttonCard" title={t('routines.empty.title')}>
      <Button onClick={onCreate}>{t('categoryView.routines.createCta')}</Button>
    </CategoryEmptyState>
  );
}

/** Files (21849:55779). No folder: the account-wide view has no "here", and the draft asks for one before it saves. */
export function FilesEmptyState({ pageName }: { pageName: string }) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  return (
    <CategoryEmptyState
      illustration="dotCard"
      dot="var(--ds-blue-400)"
      title={t('home.overview.emptyState.files.title')}
      body={t('home.overview.emptyState.files.body')}
    >
      <Button
        onClick={() => {
          logPageClickBi({ page_name: HOME_TAB_BI_PAGE, button_name: 'empty_files_create' });
          openFileDraft({ folder: '', pageName, directory: 'workspaces' });
        }}
      >
        {t('home.overview.emptyState.files.cta')}
      </Button>
    </CategoryEmptyState>
  );
}

export function AppsEmptyState({ onCreate }: { onCreate: () => void }) {
  const { t } = useTranslation();
  return (
    <CategoryEmptyState illustration="buttonCard" title={t('home.overview.emptyState.apps.title')}>
      <Button onClick={onCreate}>{t('home.overview.emptyState.apps.cta')}</Button>
    </CategoryEmptyState>
  );
}

/** Outreach, which the frames call Messages (21849:54623). */
export function OutreachEmptyState() {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const openSideChat = useOpenSideChat();
  return (
    <CategoryEmptyState
      illustration="dotCard"
      dot="var(--ds-accent-08)"
      title={t('home.overview.emptyState.messages.title')}
      body={t('home.overview.emptyState.messages.body')}
    >
      <Button
        onClick={() => {
          logPageClickBi({ page_name: HOME_TAB_BI_PAGE, button_name: 'empty_messages_contact' });
          openSideChat({ kind: 'new', message: t('home.overview.emptyState.messages.seed') });
        }}
      >
        {t('home.overview.emptyState.messages.cta')}
      </Button>
    </CategoryEmptyState>
  );
}

function OpportunityCard({ opportunity }: { opportunity: WebOpportunity }) {
  const { t } = useTranslation();
  const agent = useAgentName();
  const { logPageClickBi } = useGenericBi();
  const openSideChat = useOpenSideChat();
  const workspaces = useWorkspaces();
  const space = workspaces.status === 'ready' ? workspaces.workspaces.find((w) => w.id === opportunity.workspaceId)?.name : undefined;
  return (
    <Card variant="stone" className={styles.opportunity}>
      {space && (
        <Typography variant="heading-04" component="span" className={styles.tag}>
          {space}
        </Typography>
      )}
      <p className={`sym-type-display-04 ${styles.opportunityTitle}`}>{opportunity.title || t('goals.untitled')}</p>
      {opportunity.brief && (
        <Typography variant="heading-04" color="secondary" className={styles.brief}>
          {opportunity.brief}
        </Typography>
      )}
      <Button
        className={styles.opportunityCta}
        onClick={() => {
          logPageClickBi({ page_name: HOME_TAB_BI_PAGE, button_name: 'empty_goals_opportunity', answer: opportunity.id });
          openSideChat({ kind: 'new', message: t('home.opportunities.chatPrompt', { title: opportunity.title }) });
        }}
      >
        {t('home.overview.emptyState.goals.cta', { agent: agent.short })}
      </Button>
    </Card>
  );
}

/** "Try one of these:" over the Tasks chips — it introduces suggestions, so it is drawn only when there are some. */
function TryOne() {
  const { t } = useTranslation();
  return (
    <Typography variant="heading-04" color="secondary" className={styles.tryOne}>
      {t('home.overview.emptyState.tryOne')}
    </Typography>
  );
}

/**
 * The stacked-card pictures, drawn from the frames' own geometry. Fills are tokens, so the stack reads in dark
 * mode too; only the accent marks keep their palette steps, as the tiles do.
 */
function EmptyIllustration({ kind, dot }: { kind: Illustration; dot?: string }) {
  if (kind === 'buttonCard') {
    return (
      <svg className={styles.illustration} width="155" height="56" viewBox="0 0 155 56" aria-hidden>
        <rect x="16.2" y="0" width="122.6" height="50.3" rx="8" fill="var(--ds-surface-dark-gray)" />
        <rect x="8.3" y="8.1" width="138.3" height="46.6" rx="8" fill="var(--ds-surface-default-gray)" />
        <rect x="0" y="17.1" width="155" height="38.5" rx="8" fill="var(--ds-surface-standard-bg)" />
        <rect x="15" y="31.3" width="76" height="3" rx="1.5" fill="var(--ds-surface-default-gray)" />
        <rect x="15" y="37.3" width="63" height="3" rx="1.5" fill="var(--ds-surface-default-gray)" />
        <rect x="104" y="29.3" width="36" height="13" rx="4" fill="var(--ds-fill-accent-brand-default)" />
      </svg>
    );
  }
  return (
    <svg className={styles.illustration} width="129" height="56" viewBox="0 0 129 56" aria-hidden>
      <rect x="13.5" y="0" width="102" height="50.3" rx="8" fill="var(--ds-separator-default)" />
      <rect x="7" y="8.1" width="115.1" height="46.6" rx="8" fill="var(--ds-surface-default-gray)" />
      <rect x="0" y="17.1" width="129" height="38.5" rx="8" fill="var(--ds-surface-standard-bg)" />
      <circle cx="21" cy="36.8" r="9" fill={dot} />
      <rect x="38" y="32.3" width="79" height="3" rx="1.5" fill="var(--ds-surface-dark-gray)" />
      <rect x="38" y="38.3" width="63" height="3" rx="1.5" fill="var(--ds-surface-default-gray)" />
    </svg>
  );
}
